import { HTMLAttributes } from 'react'
import { map } from 'lodash'
import Link from 'next/link'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useAppStore } from '@/contexts/store'
import { isProtectedRoute } from '@/utils/path'

import { cn } from '@/lib/utils'

export type FooterProps = HTMLAttributes<HTMLDivElement>

const Footer = ({ className, ...props }: FooterProps) => {
  const { menu, strings } = useAppStore()

  return (
    <footer
      className={cn(
        'relative p-[1rem] pt-[11.2rem]',
        'focus-links-light',
        '[&_a:focus-visible]:-m-1 [&_a:focus-visible]:rounded-md [&_a:focus-visible]:p-1',
        className
      )}
      {...props}
    >
      <div className="rounded-[0.5rem] bg-dark py-[6rem] text-grey-light lg:py-[12rem]">
        <Container className="-mx-[1rem] w-auto max-w-none py-[4rem] lg:py-[8rem]">
          <Grid className="items-start gap-y-[2.4rem]">
            <div
              className={cn(
                'col-span-2 md:col-span-3 lg:col-span-3',
                'flex size-full flex-col space-y-[2.4rem] lg:justify-between'
              )}
            >
              <SVGIcon symbol="logo-full" className="h-[4.8rem] w-full max-w-[24.5rem]" />

              {strings?.footer?.colophon ? (
                <Typography variant="small" paragraph className="capitalize opacity-45">
                  {strings.footer.colophon}
                </Typography>
              ) : null}

              <div className="flex">
                <ul className="-ml-[0.75rem] flex space-x-[1rem] lg:mb-[0.3rem]">
                  {map(menu?.social_menu?.menu, ({ title, classes, url }, index) => (
                    <li key={`menu-item-${index}`} className="flex">
                      <Link href={url} scroll={false} className="-m-[0.5rem] p-[0.5rem]">
                        <span className="sr-only">{title}</span>
                        <SVGIcon symbol={classes} className="size-[2.4rem] shrink-0 fill-current" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <Grid className={cn('hidden', 'lg:col-span-6 lg:grid lg:grid-cols-6 lg:items-start')}>
              <div className={cn('col-span-2 lg:col-span-3', 'grid gap-y-[1.6rem]')}>
                {strings?.footer?.sitemap_label ? (
                  <Typography variant="breadcrumbs" paragraph className="capitalize opacity-45">
                    {strings.footer.sitemap_label}
                  </Typography>
                ) : null}

                <ul className="grid gap-y-[0.8rem]">
                  {map(menu?.footer_menu_01?.menu, ({ title, url }, index) => (
                    <li key={`menu-item-${index}`} className="flex">
                      <Link
                        href={url}
                        scroll={false}
                        // className="transition-opacity duration-500 ease-out-cubic hover:opacity-50"
                      >
                        <Typography variant="standard" className="capitalize">
                          {title}
                        </Typography>
                      </Link>
                    </li>
                  ))}
                </ul>

                <hr className="mb-[1.6rem] h-[0.1rem] border-0 bg-current opacity-45" />
              </div>

              <div className={cn('col-span-2 lg:col-span-3', 'grid items-start gap-y-[1.6rem]')}>
                {strings?.footer?.collections_label ? (
                  <Typography variant="breadcrumbs" paragraph className="capitalize opacity-45">
                    {strings.footer.collections_label}
                  </Typography>
                ) : null}

                <ul className="grid gap-y-[0.8rem]">
                  {map(menu?.footer_menu_02?.menu, ({ title, url }, index) => (
                    <li key={`menu-item-${index}`} className="flex">
                      <Link href={url} scroll={false}>
                        <Typography variant="standard" className="capitalize">
                          {title}
                        </Typography>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={cn('col-span-2 lg:col-span-3', 'grid items-start gap-y-[1.6rem]')}>
                <ul className="grid gap-y-[0.8rem]">
                  {map(menu?.footer_menu_03?.menu, ({ title, url }, index) => (
                    <li key={`menu-item-${index}`} className="flex">
                      <Link href={url} scroll={false}>
                        <Typography variant="standard" className="capitalize">
                          {title}
                        </Typography>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div
                className={cn(
                  'col-span-2 lg:col-span-3',
                  'grid size-full items-start gap-y-[1.6rem]'
                )}
              >
                {strings?.footer?.architects_label ? (
                  <Typography
                    variant="breadcrumbs"
                    paragraph
                    className="mt-[0.8rem] capitalize opacity-45"
                  >
                    {strings.footer.architects_label}
                  </Typography>
                ) : null}

                <ul className="mt-auto grid gap-y-[0.8rem]">
                  {map(menu?.footer_menu_04?.menu, ({ title, url }, index) => (
                    <li key={`menu-item-${index}`} className="flex">
                      <Link href={url} scroll={false} prefetch={!isProtectedRoute(url)}>
                        <Typography variant="standard" className="capitalize">
                          {title}
                        </Typography>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Grid>

            <div className={cn('col-span-2 lg:col-span-3', 'grid items-start gap-y-[1.6rem]')}>
              {strings?.footer?.legals_label ? (
                <Typography variant="breadcrumbs" paragraph className="capitalize opacity-45">
                  {strings.footer.legals_label}
                </Typography>
              ) : null}

              <ul className="grid gap-y-[0.8rem]">
                {map(menu?.legals_menu?.menu, ({ title, url }, index) => (
                  <li key={`menu-item-${index}`} className="flex">
                    <Link href={url} scroll={false}>
                      <Typography variant="breadcrumbs" className="capitalize">
                        {title}
                      </Typography>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </Grid>
        </Container>
      </div>
    </footer>
  )
}

export default Footer
